import { Directive, forwardRef, Input, HostListener } from '@angular/core';
import { NG_VALIDATORS, AbstractControl, ValidationErrors, Validator, UntypedFormControl } from '@angular/forms';
import { environment } from 'src/environments/environment';
import { AppService } from 'src/app/app.service';

import * as _ from 'lodash';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);

function nonEmptyFactory() {
	return (c: UntypedFormControl) => {
		return !c.value || c.value.trim() !== '' ? null : {
			nonEmpty: {
				valid: false
			}
		};
	};
}

@Directive({
    selector: '[nonEmpty][ngModel],[nonEmpty][formControl]',
    providers: [
        { provide: NG_VALIDATORS, useExisting: forwardRef(() => NonemptyValidator), multi: true }
    ],
    standalone: false
})
export class NonemptyValidator {

	validator: Function;

	constructor() {
		this.validator = nonEmptyFactory();
	}

	validate(c: UntypedFormControl) {
		return this.validator(c);
	}
}

@Directive({
    selector: '[validIdCard]',
    providers: [
        { provide: NG_VALIDATORS, useExisting: IdCardValidator, multi: true }
    ],
    standalone: false
})
export class IdCardValidator implements Validator {

	validate(c: UntypedFormControl): ValidationErrors | null {
		return IdCardValidator.validateIdCard(c);
	}
	static validateIdCard(control: UntypedFormControl): ValidationErrors {
		if (!IdCardValidator.isValidIsraelIdCard(control.value)) {
			return { creditCard: 'VALIDATIONS.ID_CARD' };
		}
		return null
	}
	static isValidIsraelIdCard(value) {
		if (isNaN(value) || Number(value) === 0) return false;
		var id = String(value).trim();
		if (id.length > 9 || id.length < 5) return false;

		// Pad string with zeros up to 9 digits
		id = id.length < 9 ? ("00000000" + id).slice(-9) : id;

		return Array.from(id, Number)
			.reduce((counter, digit, i) => {
				const step = digit * ((i % 2) + 1);
				return counter + (step > 9 ? step - 9 : step);
			}) % 10 === 0;
	}
}

@Directive({
    selector: '[validName]',
    providers: [
        { provide: NG_VALIDATORS, useExisting: NameFieldValidator, multi: true }
    ],
    standalone: false
})
export class NameFieldValidator implements Validator {
	validate(c: UntypedFormControl): ValidationErrors | null {
		return NameFieldValidator.validateNameField(c);
	}
	static validateNameField(control: UntypedFormControl): ValidationErrors {
		let val = control.value;
		if (!val || !val.length) return;
		if (!/^[a-zA-Z\u0590-\u05FF\- ]*$/.test(val)) {
			return { nameField: 'VALIDATIONS.NAME_FIELD' };
		}
		return null;
	}
}

@Directive({
    selector: '[validZip]',
    providers: [
        { provide: NG_VALIDATORS, useExisting: ZipCodeValidator, multi: true }
    ],
    standalone: false
})

export class ZipCodeValidator implements Validator {

	validate(c: UntypedFormControl): ValidationErrors | null {
		if (!ZipCodeValidator.isValidZipCode(c)) {
			return { zipCode: 'VALIDATIONS.ZIPCODE' };
		}
		return null
	}

	static isValidZipCode(c: UntypedFormControl) {
		let valid = true, val = c.value, parsedVal;
		if (!val || val.length < 5) valid = false;
		else if (isNaN(val)) {
			let aval = val.split('-');
			if (aval.length != 2 || aval[0].length != 5 && aval[1].length != 4) {
				valid = false;
			}
		}
		else if (val.length == 5) { valid = true; }
		else if (val.length == 9) {
			parsedVal = `${val.substring(0, 5)}-${val.substring(5, 9)}`;
			c.setValue(parsedVal);
		}
		else valid = false;

		return valid;
	}

}

@Directive({
    selector: '[validCell]',
    providers: [
        { provide: NG_VALIDATORS, useExisting: CellFieldValidator, multi: true }
    ],
    standalone: false
})
export class CellFieldValidator implements Validator {

	constructor(
		public app: AppService
	) {
	}
	validate(c: UntypedFormControl): ValidationErrors | null {
		return CellFieldValidator.validateCellField(c, this.app.region);
	}
	static validateCellField(control: UntypedFormControl, region): ValidationErrors {
		let val = control.value;
		if (!val || !val.length) return;
		let parsedVal = val.replace(/\D/g, '');
		if (val != parsedVal) control.setValue(parsedVal);

		switch (region) {
			case 'AU':
				if (parsedVal.length < 9 || !/^[0-9]*$/.test(parsedVal)) {
					return { cellField: 'VALIDATIONS.CELL_FIELD' };
				}
				break;
			case 'US':
				if (parsedVal.length != 10 || !/^[0-9]*$/.test(parsedVal)) {
					return { cellField: 'VALIDATIONS.CELL_FIELD' };
				}
				break;
			default:
				if (parsedVal.length != 10 || !/^[0-9]*$/.test(parsedVal)) {
					return { cellField: 'VALIDATIONS.CELL_FIELD' };
				}
				if (region == 'IL' && parsedVal.indexOf('05') != 0) {
					return { cellField: 'VALIDATIONS.CELL_FIELD_MSG' };
				}
		}
	}
}

@Directive({
    selector: '[validCreditCard]',
    providers: [
        { provide: NG_VALIDATORS, useExisting: CreditCardValidator, multi: true }
    ],
    standalone: false
})
export class CreditCardValidator implements Validator {
	@Input() public ccInfo: any = {};

	validate(c: UntypedFormControl): ValidationErrors | null {
		return CreditCardValidator.validateCcNumber(c, this.ccInfo);
	}
	static validateCcNumber(control: UntypedFormControl, ccInfo): ValidationErrors {


		let ccType = CreditCardValidator.GetCreditCardType(control.value);

		ccInfo.brand = ccType.type || 'cc';
		ccInfo.brandName = ccType.name;

		if (!ccType.type) {
			return { creditCard: 'VALIDATIONS.CREDIT_CARD' };
		}
		return null
	}

	static GetCreditCardType(number) {
		// visa
		if (number) number += ""
		else number = "";


		if (number.length >= 8 && number.length <= 9) {
			let on = (number.length == 8 ? '0' + number : number).split("");
			let sum = 0;
			for (let i = 0; i < 9; i++) {
				sum += Number(on[i]) * (9 - i);
			}
			if (sum % 11 == 0) return { name: "Isracard", type: "cc_mastercard" };
		}

		var re = new RegExp("^4");
		if (number.match(re) != null) return { name: "Visa", type: "cc_visa" };

		// Mastercard
		// Updated for Mastercard 2017 BINs expansion
		if (/^(5[1-5][0-9]{14}|2(22[1-9][0-9]{12}|2[3-9][0-9]{13}|[3-6][0-9]{14}|7[0-1][0-9]{13}|720[0-9]{12}))$/.test(number))
			return { name: "Mastercard", type: "cc_mastercard" };

		// AMEX
		re = new RegExp("^3[47]");
		if (number.match(re) != null) return { name: "AMEX", type: "cc_mastercard" };


		// Discover
		re = new RegExp("^(6011|622(12[6-9]|1[3-9][0-9]|[2-8][0-9]{2}|9[0-1][0-9]|92[0-5]|64[4-9])|65)");
		if (number.match(re) != null) return { name: "Discover", type: "cc_discover" };


		// Diners
		re = new RegExp("^36");
		if (number.match(re) != null) return { name: "Diners", type: "cc_diners" };


		// Diners - Carte Blanche
		re = new RegExp("^30[0-5]");
		if (number.match(re) != null) return { name: "Diners - Carte Blanche", type: "cc_diners" };


		// JCB
		re = new RegExp("^35(2[89]|[3-8][0-9])");
		if (number.match(re) != null) return { name: "JCB", type: "cc_jcb" };

		// Visa Electron
		re = new RegExp("^(4026|417500|4508|4844|491(3|7))");
		if (number.match(re) != null) return { name: "Visa Electron", type: "cc_visa" };

		return {};
	}

}


@Directive({
    selector: '[validCardExpiration]',
    providers: [
        { provide: NG_VALIDATORS, useExisting: CreditCardExpirationValidator, multi: true }
    ],
    standalone: false
})
export class CreditCardExpirationValidator implements Validator {
	@Input() public ccInfo: any = {};

	@HostListener('keydown', ['$event']) onKeyDown(e) {
		this.isBackspace = e.code == 'Backspace' || e.key == "Backspace";
	}
	isBackspace: boolean = false;

	validate(c: UntypedFormControl): ValidationErrors | null {
		return this.validateExpiration(c);
	}

	validateExpiration(control: UntypedFormControl): ValidationErrors {
		if (this.isBackspace) return null;

		let value = control?.value;
		if (!value) return null;

		let numericValue = value.replace(/\D/g, '');
		if (numericValue.length == 6 ) {//fix auto complete in format mm/yyyy valid untill 2099
			if (numericValue.slice(2, 4) == "20") numericValue = numericValue.slice(0, 2) + numericValue.slice(4);
		} else if (numericValue.length != 4) return this.validationError();

		numericValue = numericValue.slice(0, 2) + '-' + numericValue.slice(2);

		const parsedDate = dayjs(numericValue, 'MM-YY');

		if (!parsedDate.isValid() || parsedDate.isBefore(dayjs())) {
			this.prepareValue(control, value, numericValue, false);
			return this.validationError();
		}

		this.ccInfo.expMonth = parsedDate.format('MM');
		this.ccInfo.expYear = parsedDate.format('YYYY');

		if (value != numericValue) return this.prepareValue(control, value, numericValue, true);
	}

	prepareValue(control: UntypedFormControl, value, newValue, isValid) {
		if (value != newValue) control.setValue(newValue);
		if (!isValid) return null;
	}

	validationError() {
		delete this.ccInfo.expMonth;
		delete this.ccInfo.expYear;
		return { creditCardExpiration: 'VALIDATIONS.CREDIT_CARD_EXPIRATION' }
	}
}
